import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Settings -> Easeetask (in meniu: "Dailee"), calea de citire.
 *
 * Perechile vechi, din `application/controllers/settings/Easeetask.php`:
 *
 *   index / user_organisations -> GET /settings/easeetask
 *   project_notifications      -> GET /settings/easeetask/project_notifications
 *   getOrganisationsList       -> GET /settings/easeetask/organisations/table
 *   getUsersBusinessHours      -> GET /settings/easeetask/user_organisations/table
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`.
 */

export interface EaseetaskTab {
  key: 'project_notifications' | 'organisations' | 'user_organisations'
  label: string
  /** Are omul dreptul randului asta? Bara arata numai ce e adevarat. */
  shown: boolean
}

export interface EaseetaskOrganisationOption {
  id: number
  name: string
  /** 1 = organizatia principala a omului; ea se alege singura. */
  main: number
}

export interface EaseetaskShell {
  title: string
  moduleUrl: string
  tabs: EaseetaskTab[]
  organisations: EaseetaskOrganisationOption[]
}

export interface ProjectNotifications {
  active: boolean
  /** Fals = furnizorul n-are inca niciun rand scris; tot stins inseamna. */
  exists: boolean
}

export interface EaseetaskTableRequest {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

export interface OrganisationRow {
  id: number
  name: string
  email: string
  phone: string
  website: string
  private: number
  role: string
  main: number
  DT_RowId: string
}

export interface BusinessHoursRow {
  user_id: number
  userName: string
  userFirstname: string
  start: string
  end: string
  DT_RowId: string
}

export interface EaseetaskTablePage<T> {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: T[]
}

export function easeetaskTableParams(r: EaseetaskTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

export const easeetaskShellQuery = queryOptions({
  queryKey: ['settings', 'easeetask'],
  queryFn: () => apiGet<EaseetaskShell>('/settings/easeetask'),
})

export const projectNotificationsQuery = queryOptions({
  queryKey: ['settings', 'easeetask', 'project_notifications'],
  queryFn: () => apiGet<ProjectNotifications>('/settings/easeetask/project_notifications'),
})

export function organisationTableQuery(r: EaseetaskTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'easeetask', 'organisations', r],
    queryFn: () =>
      apiGet<EaseetaskTablePage<OrganisationRow>>(
        `/settings/easeetask/organisations/table?${easeetaskTableParams(r).toString()}`,
      ),
  })
}

export function businessHoursTableQuery(organisation: number, r: EaseetaskTableRequest) {
  const params = easeetaskTableParams(r)
  /* Numele pe care il trimitea DataTables in portal. */
  params.set('organisationFilter', String(organisation))

  return queryOptions({
    queryKey: ['settings', 'easeetask', 'user_organisations', organisation, r],
    queryFn: () =>
      apiGet<EaseetaskTablePage<BusinessHoursRow>>(
        `/settings/easeetask/user_organisations/table?${params.toString()}`,
      ),
    enabled: organisation > 0,
  })
}

/* -------------------------------------------------------------------------
 * Scrierile (mutate pe 12 septembrie 2026): `create`,
 * `rebuild_project_notifications_configuration`, `createOrganisation`,
 * `updateOrganisation`, `deleteOrganisation`, `addMember`,
 * `updateUserBusinessHours`.
 * ---------------------------------------------------------------------- */

export type OrganisationForm = {
  id: number
  organisation_name: string
  email: string
  website: string
  phone: string
  address: string
  description: string
  private: number
  default: number
  role: string
}

export function organisationFormQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'easeetask', 'organisation', id],
    queryFn: () => apiGet<{ organisation: OrganisationForm }>(`/settings/easeetask/organisations/${id}`),
    enabled: id > 0,
  })
}

export function createOrganisation(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number; message: string }>('/settings/easeetask/organisations', fields)
}

export function updateOrganisation(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number; message: string }>(`/settings/easeetask/organisations/${id}`, fields)
}

export function removeOrganisation(id: number) {
  return apiPost<{ saved: boolean; id: number }>(`/settings/easeetask/organisations/${id}/remove`, {})
}

/** `addMember`: un angajat existent intra (sau revine) in organizatie; un strain primeste o invitatie. */
export type MemberAdded = { saved: boolean; kind: 'member' | 'invitation'; changed: boolean; sent: boolean; message: string }

export function addMember(organisation: number, fields: Record<string, PostValue>) {
  return apiPost<MemberAdded>(`/settings/easeetask/organisations/${organisation}/members`, fields)
}

/** `create` (intrerupatorul notificarilor) si `rebuild_project_notifications_configuration`. */
export function saveProjectNotifications(active: boolean) {
  return apiPost<{ saved: boolean; active: boolean }>('/settings/easeetask/project_notifications', { status: active ? 'true' : 'false' })
}

export function rebuildProjectNotifications() {
  return apiPost<{ ok: boolean; action: string; message?: string }>('/settings/easeetask/project_notifications/rebuild', {})
}

/** `updateUserBusinessHours/{user}`: editarea in celula, `columnName` Start|End si `value` HH:MM. */
export function saveBusinessHours(user: number, columnName: 'Start' | 'End', value: string) {
  return apiPost<{ status: string; userId: number }>(`/settings/easeetask/user_organisations/${user}/hours`, { columnName, value })
}
